import { ServerStatusNotice } from '@/components/common/ServerStatusNotice';
import React, { useEffect, useRef, useState } from 'react';
import useSWR, { mutate as globalMutate } from 'swr';
import { useNavigate } from 'react-router-dom';
import {
  CheckCheck,
  Copy,
  Loader2,
  LogOut,
  Pin,
  Plus,
  RefreshCw,
  Search,
  Send,
  Trash2,
  UserPlus,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useT } from '@/contexts/I18nContext';
import { usePageTitle } from '@/contexts/PageHeaderContext';
import { useCurrentZone, ZONES_SWR_KEY } from '@/hooks/useCurrentZone';
import { BranchRequiredState } from '@/components/common/BranchRequiredState';
import { useCan } from '@/hooks/useCan';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useAppActions } from '@/app/appContext';
import {
  getZoneAccess,
  getPinInvite,
  reissuePinInvite,
  createInvite,
  revokeInvite,
  updateInviteRole,
  removeMember,
  updateMember,
  addMember,
  searchUsers,
  ROLE_RANK,
  type InviteDto,
  type MemberDto,
  type UserSearchHit,
} from '@/api/invites';
import { extractApiError } from '@/utils/apiError';
import { Tooltip } from '@/components/ui/tooltip';

const ALL_ROLES = ['zone_employee', 'zone_manager', 'zone_admin'] as const;

// ── Helpers ───────────────────────────────────────────────────────────────────

function buildInviteUrl(token: string) {
  return `${window.location.origin}/invite/${token}`;
}

function allowedRoles(maxRank: number) {
  return ALL_ROLES.filter(r => ROLE_RANK[r] <= maxRank);
}

// Placeholder rows shown while the zone-access request is in flight, so the
// links/members cards never flash "Активных ссылок нет" before the data lands.
function AccessRowSkeleton({ count }: { count: number }) {
  return (
    <div className="space-y-2" aria-hidden>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="h-11 animate-pulse rounded-md bg-muted" />
      ))}
    </div>
  );
}

// ── Invite row ────────────────────────────────────────────────────────────────

function InviteRow({
  invite,
  maxRank,
  zoneId,
  onChanged,
}: {
  invite: InviteDto;
  maxRank: number;
  zoneId: string;
  onChanged: () => void;
}) {
  const { t } = useT();
  const [copied, setCopied] = useState(false);
  const [copiedTg, setCopiedTg] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reissueOpen, setReissueOpen] = useState(false);
  const isPin = invite.purpose === 'pin_message';
  const url = buildInviteUrl(invite.token);
  const miniappUrl = invite.miniapp_url;
  const expiresLabel = new Date(invite.expires_at).toLocaleDateString();

  function handleCopy() {
    navigator.clipboard.writeText(url).then(
      () => { setCopied(true); setTimeout(() => setCopied(false), 2000); },
      () => {},
    );
  }

  function handleCopyTg() {
    if (!miniappUrl) return;
    navigator.clipboard.writeText(miniappUrl).then(
      () => { setCopiedTg(true); setTimeout(() => setCopiedTg(false), 2000); },
      () => {},
    );
  }

  async function handleRoleChange(e: React.ChangeEvent<HTMLSelectElement>) {
    const newRole = e.target.value;
    setError(null);
    setBusy(true);
    try {
      await updateInviteRole(invite.id, newRole);
      onChanged();
    } catch (err) {
      setError(extractApiError(err, t('error.general')));
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete() {
    setError(null);
    setBusy(true);
    try {
      await revokeInvite(invite.id);
      onChanged();
    } catch (err) {
      setError(extractApiError(err, t('error.general')));
      setBusy(false);
    }
  }

  async function doReissue() {
    setError(null);
    setBusy(true);
    try {
      await reissuePinInvite(zoneId);
      setReissueOpen(false);
      onChanged();
    } catch (err) {
      setError(extractApiError(err, t('error.general')));
    } finally {
      setBusy(false);
    }
  }

  const roleOptions = allowedRoles(maxRank);
  const roleEditable = roleOptions.includes(invite.role as typeof ALL_ROLES[number]);

  return (
    <div className="flex flex-col gap-1">
      <div className="flex flex-col gap-2 rounded-md border px-3 py-2 text-sm">
        {/* Top row: role + meta badges + actions. Stays one row on
            both mobile and desktop (with flex-wrap as a fallback if
            badges accumulate); the URL gets its own row below so it's
            always fully readable on narrow screens. */}
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          {roleEditable ? (
            <div className="inline-block w-auto min-w-[8rem]">
              <Select value={invite.role} onChange={handleRoleChange} disabled={busy} className="h-7 text-xs">
                {roleOptions.map(r => (
                  <option key={r} value={r}>{t(`role.${r}`)}</option>
                ))}
              </Select>
            </div>
          ) : (
            <span className="text-xs font-medium">{t(`role.${invite.role}`)}</span>
          )}
          <span className="text-[11px] text-muted-foreground">
            {isPin
              ? t('access.pin.autoRenew')
              : t('access.expiresOn', { date: expiresLabel })}
          </span>
          {(invite.is_persistent || isPin) && (invite.redemption_count ?? 0) > 0 && (
            <span className="text-[11px] text-muted-foreground">
              {t('access.persistent.count', { n: invite.redemption_count })}
            </span>
          )}
          {isPin && (
            <Tooltip content={t('access.pin.hint')}><span
              className="inline-flex items-center gap-1 rounded bg-amber-100 px-1.5 py-0.5 text-[11px] text-amber-800 dark:bg-amber-900/40 dark:text-amber-200"
            >
              <Pin className="size-3" /> {t('access.pin.badge')}
            </span></Tooltip>
          )}
          <div className="ml-auto flex shrink-0 gap-1">
            {miniappUrl && (
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={handleCopyTg}
                title={t('access.copyTelegramLink')}
              >
                {copiedTg
                  ? <CheckCheck className="h-3.5 w-3.5 text-green-500" />
                  : <Send className="h-3.5 w-3.5 text-[#229ED9]" />}
              </Button>
            )}
            <Button variant="ghost" size="icon-sm" onClick={handleCopy} title={t('access.copyLink')}>
              {copied ? <CheckCheck className="h-3.5 w-3.5 text-green-500" /> : <Copy className="h-3.5 w-3.5" />}
            </Button>
            {isPin ? (
              <Button variant="ghost" size="icon-sm" onClick={() => setReissueOpen(true)} disabled={busy} title={t('access.pin.reissue')}>
                <RefreshCw className="h-3.5 w-3.5" />
              </Button>
            ) : (
              <Button variant="ghost" size="icon-sm" onClick={handleDelete} disabled={busy} title={t('access.revoke')}>
                <Trash2 className="h-3.5 w-3.5 text-destructive" />
              </Button>
            )}
          </div>
        </div>
        {/* URL row — break-all so the long invite token wraps into
            the available width instead of getting cropped (was
            `truncate min-w-0 flex-1` before, which on mobile gave
            the URL ~40px and showed just "https://my..."). */}
        <span className="break-all font-mono text-xs text-muted-foreground">
          {url}
        </span>
      </div>
      {error && <p className="px-3 text-xs text-destructive">{error}</p>}
      <Dialog open={reissueOpen} onOpenChange={v => !v && setReissueOpen(false)}>
        <DialogContent className="relative">
          <DialogHeader>
            <DialogTitle>{t('access.pin.reissue')}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">{t('access.pinReissueConfirm')}</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReissueOpen(false)} disabled={busy}>
              {t('common.cancel')}
            </Button>
            <Button onClick={doReissue} disabled={busy}>
              {busy ? t('common.saving') : t('access.pin.reissue')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ── Create-invite dialog ──────────────────────────────────────────────────────

interface CreateInviteDialogProps {
  open: boolean;
  zoneId: string;
  maxRank: number;
  onClose: () => void;
  onCreated: (url: string) => void;
}

function CreateInviteDialog({ open, zoneId, maxRank, onClose, onCreated }: CreateInviteDialogProps) {
  const { t } = useT();
  const roleOptions = allowedRoles(maxRank);
  const [role, setRole] = useState<string>(roleOptions[0] ?? 'zone_employee');
  // Default: multi-use, valid forever (3650d ≈ 10y — practical "бессрочно").
  const [oneShot, setOneShot] = useState(false);
  const [expiresDays, setExpiresDays] = useState<number>(3650);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      const fallback = roleOptions[0] ?? 'zone_employee';
      setRole(fallback);
      setOneShot(false);
      setExpiresDays(3650);
      setError(null);
    }
  }, [open]);

  async function handleSubmit() {
    setError(null);
    setSaving(true);
    try {
      const res = await createInvite(zoneId, role, expiresDays, !oneShot);
      onCreated(buildInviteUrl(res.data.token));
      onClose();
    } catch (err) {
      setError(extractApiError(err, t('error.general')));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="relative">
        <DialogHeader>
          <DialogTitle>{t('access.create.title')}</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="invite-role">{t('access.create.role')}</Label>
            <Select id="invite-role" value={role} onChange={e => setRole(e.target.value)}>
              {roleOptions.map(r => (
                <option key={r} value={r}>{t(`role.${r}`)}</option>
              ))}
            </Select>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="invite-expires">{t('access.create.expires')}</Label>
            <Select id="invite-expires" value={expiresDays} onChange={e => setExpiresDays(Number(e.target.value))}>
              <option value={1}>{t('access.create.expires1d')}</option>
              <option value={7}>{t('access.create.expires7d')}</option>
              <option value={30}>{t('access.create.expires30d')}</option>
              <option value={90}>{t('access.create.expires90d')}</option>
              <option value={3650}>{t('access.create.expiresForever')}</option>
            </Select>
          </div>
          <label className="inline-flex cursor-pointer items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={oneShot}
              onChange={e => setOneShot(e.target.checked)}
              className="size-3.5"
            />
            <span>{t('access.create.oneShot')}</span>
          </label>
          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t('common.cancel')}</Button>
          <Button onClick={handleSubmit} disabled={saving}>
            {saving ? t('common.saving') : t('access.create.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Member row ────────────────────────────────────────────────────────────────

function MemberRow({
  member,
  zoneId,
  maxRank,
  canManage,
  isSelf,
  canEditOwnRole,
  onChanged,
  onLeft,
}: {
  member: MemberDto;
  zoneId: string;
  maxRank: number;
  canManage: boolean;
  isSelf: boolean;
  canEditOwnRole: boolean;
  onChanged: () => void;
  onLeft: () => void;
}) {
  const { t } = useT();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const roleEditable = canManage && (!isSelf || canEditOwnRole);
  const showRemoveButton = !isSelf && canManage;
  const roleOptions = allowedRoles(maxRank);

  async function handleRemove() {
    setError(null);
    setBusy(true);
    try {
      await removeMember(zoneId, member.id);
      onChanged();
    } catch (err) {
      setError(extractApiError(err, t('error.general')));
      setBusy(false);
    }
  }

  async function handleLeave() {
    if (!window.confirm(t('access.leaveConfirm'))) return;
    setError(null);
    setBusy(true);
    try {
      await removeMember(zoneId, member.id);
      onLeft();
    } catch (err) {
      setError(extractApiError(err, t('error.general')));
      setBusy(false);
    }
  }

  async function handleRoleChange(e: React.ChangeEvent<HTMLSelectElement>) {
    setError(null);
    try {
      await updateMember(zoneId, member.id, e.target.value);
      onChanged();
    } catch (err) {
      setError(extractApiError(err, t('error.general')));
    }
  }

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center justify-between gap-3 rounded-md border px-3 py-2 text-sm">
        <div className="flex items-center gap-2 min-w-0">
          <span className="font-medium">
            {member.username}
            {isSelf && <span className="ml-1.5 text-xs text-muted-foreground">{t('access.you')}</span>}
          </span>
          {roleEditable && roleOptions.length > 0 ? (
            <div className="inline-block w-auto min-w-[8rem]">
              <Select value={member.role} onChange={handleRoleChange} className="h-7 text-xs">
                {roleOptions.map(r => (
                  <option key={r} value={r}>{t(`role.${r}`)}</option>
                ))}
                {/* Role already higher than caller's ceiling — show but disable. */}
                {!roleOptions.includes(member.role as typeof ALL_ROLES[number]) && (
                  <option key={member.role} value={member.role} disabled>
                    {t(`role.${member.role}`)}
                  </option>
                )}
              </Select>
            </div>
          ) : (
            <span className="text-xs text-muted-foreground">{t(`role.${member.role}`)}</span>
          )}
        </div>
        {isSelf ? (
          <Button variant="ghost" size="icon-sm" onClick={handleLeave} disabled={busy} title={t('access.leave')}>
            <LogOut className="h-3.5 w-3.5 text-destructive" />
          </Button>
        ) : showRemoveButton ? (
          <Button variant="ghost" size="icon-sm" onClick={handleRemove} disabled={busy} title={t('access.remove')}>
            <Trash2 className="h-3.5 w-3.5 text-destructive" />
          </Button>
        ) : null}
      </div>
      {error && <p className="px-3 text-xs text-destructive">{error}</p>}
    </div>
  );
}

// ── Add-user dialog ───────────────────────────────────────────────────────────

interface AddUserDialogProps {
  open: boolean;
  zoneId: string;
  maxRank: number;
  isSuperuser: boolean;
  onClose: () => void;
  onChanged: () => void;
}

function AddUserDialog({ open, zoneId, maxRank, isSuperuser, onClose, onChanged }: AddUserDialogProps) {
  const { t } = useT();
  const roleOptions = allowedRoles(maxRank);
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [role, setRole] = useState<string>(roleOptions[0] ?? 'zone_employee');
  const [pendingCreate, setPendingCreate] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setQuery('');
      setDebouncedQuery('');
      setRole(roleOptions[0] ?? 'zone_employee');
      setPendingCreate(null);
      setPassword('');
      setError(null);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  useEffect(() => {
    const id = window.setTimeout(() => setDebouncedQuery(query.trim()), 200);
    return () => window.clearTimeout(id);
  }, [query]);

  const swrKey = open && debouncedQuery ? `user-search:${debouncedQuery}` : null;
  const { data: searchData, isLoading } = useSWR(
    swrKey,
    () => searchUsers(debouncedQuery).then(r => r.data.results),
    { keepPreviousData: true },
  );
  const matches: UserSearchHit[] = searchData ?? [];
  const hasExactMatch = matches.some(m => m.username.toLowerCase() === debouncedQuery.toLowerCase());
  const canOfferCreate = isSuperuser && debouncedQuery !== '' && !hasExactMatch && !isLoading;

  async function handleAttach(username: string, opts: { password?: string } = {}) {
    setError(null);
    setSaving(true);
    try {
      await addMember(zoneId, { username, role, ...opts });
      onChanged();
      onClose();
    } catch (err) {
      setError(extractApiError(err, t('error.general')));
    } finally {
      setSaving(false);
    }
  }

  function startCreate(username: string) {
    setPendingCreate(username);
    setPassword('');
    setError(null);
  }

  async function handleCreate() {
    if (!pendingCreate || !password) return;
    await handleAttach(pendingCreate, { password });
  }

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="relative">
        <DialogHeader>
          <DialogTitle>
            {pendingCreate ? t('access.add.createTitle') : t('access.add.title')}
          </DialogTitle>
        </DialogHeader>
        {!pendingCreate && (
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="add-user-role">{t('access.add.role')}</Label>
              <Select id="add-user-role" value={role} onChange={e => setRole(e.target.value)}>
                {roleOptions.map(r => (
                  <option key={r} value={r}>{t(`role.${r}`)}</option>
                ))}
              </Select>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="add-user-search">
                {isSuperuser ? t('access.add.searchSuperuser') : t('access.add.search')}
              </Label>
              <div className="relative">
                <Search className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
                <input
                  ref={inputRef}
                  id="add-user-search"
                  type="text"
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                  placeholder={t('access.add.searchPlaceholder')}
                  className="h-9 w-full rounded-md border bg-background pl-8 pr-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring/50 focus-visible:border-ring"
                  autoComplete="off"
                />
              </div>
            </div>
            <div className="max-h-56 overflow-auto rounded-md border bg-background">
              {isLoading && (
                <div className="flex items-center justify-center gap-2 py-3 text-xs text-muted-foreground">
                  <Loader2 className="size-3.5 animate-spin" /> {t('common.loading')}
                </div>
              )}
              {!isLoading && matches.length === 0 && debouncedQuery && (
                <div className="py-3 text-center text-xs text-muted-foreground">
                  {isSuperuser ? t('access.add.noMatches') : t('access.add.noMatchesScoped')}
                </div>
              )}
              {!isLoading && matches.map(u => (
                <button
                  key={u.id}
                  type="button"
                  disabled={saving}
                  onClick={() => handleAttach(u.username)}
                  className="flex w-full items-center gap-2 border-t px-2 py-1.5 text-left text-sm first:border-t-0 transition-colors hover:bg-muted hover:text-foreground disabled:opacity-50"
                >
                  <UserPlus className="size-4 shrink-0 text-muted-foreground" />
                  <span className="flex-1 min-w-0 truncate">
                    <span className="font-medium">{u.username}</span>
                    {u.email && <span className="ml-2 text-xs text-muted-foreground">{u.email}</span>}
                  </span>
                </button>
              ))}
              {canOfferCreate && (
                <button
                  type="button"
                  onClick={() => startCreate(debouncedQuery)}
                  className="flex w-full items-center gap-2 border-t px-2 py-1.5 text-left text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                >
                  <Plus className="size-4 shrink-0" />
                  <span className="flex-1 min-w-0 truncate">
                    {t('access.add.createWith', { username: debouncedQuery })}
                  </span>
                </button>
              )}
            </div>
            {error && <p className="text-sm text-destructive">{error}</p>}
          </div>
        )}
        {pendingCreate && (
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="create-user-username">{t('access.add.usernameLabel')}</Label>
              <Input id="create-user-username" value={pendingCreate} readOnly />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="create-user-password">{t('access.add.passwordLabel')}</Label>
              <Input
                id="create-user-password"
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                autoComplete="new-password"
                onKeyDown={e => { if (e.key === 'Enter') handleCreate(); }}
              />
            </div>
            {error && <p className="text-sm text-destructive">{error}</p>}
          </div>
        )}
        <DialogFooter>
          {pendingCreate ? (
            <>
              <Button variant="outline" onClick={() => setPendingCreate(null)}>{t('common.back')}</Button>
              <Button onClick={handleCreate} disabled={saving || !password}>
                {saving ? t('common.saving') : t('access.add.createSubmit')}
              </Button>
            </>
          ) : (
            <Button variant="outline" onClick={onClose}>{t('common.cancel')}</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Access page ───────────────────────────────────────────────────────────────

const AccessPage: React.FC = () => {
  const { t } = useT();
  const { zoneId } = useCurrentZone();
  const canInvite = useCan('zone.invite', zoneId);
  const { user } = useCurrentUser();
  const isSuperuser = user?.is_superuser === true;
  const currentUsername = user?.username;
  const navigate = useNavigate();
  const { setZone, setCamera, setSelectedCamera, setFrame } = useAppActions();
  usePageTitle(t('access.title'), t('access.subtitle'));

  const swrKey = zoneId ? `zone-access:${zoneId}` : null;
  const { data, mutate, error, isLoading } = useSWR(swrKey, () => getZoneAccess(zoneId!).then(r => r.data));
  // First load (no data yet): show skeleton rows, not the "no links / no members"
  // empty states — those read as a definitive answer while we're still fetching.
  const loading = isLoading && !data;

  // Pin invite lives in its own get-or-create endpoint so a fresh zone
  // gets one materialised on first page load. Merging the result into
  // the invite list happens in `combinedInvites` below.
  const pinKey = zoneId && canInvite ? `zone-pin-invite:${zoneId}` : null;
  const { data: pinInvite, mutate: mutatePin } = useSWR(
    pinKey,
    () => getPinInvite(zoneId!).then(r => r.data),
    { revalidateOnFocus: false },
  );

  const [createOpen, setCreateOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [lastCreatedUrl, setLastCreatedUrl] = useState<string | null>(null);

  if (!zoneId) {
    return <BranchRequiredState />;
  }

  if (error && !data) {
    return <ServerStatusNotice error={error} onRetry={() => mutate()} />;
  }

  const members = data?.members ?? [];
  const maxRank = data?.max_role_rank ?? 0;
  const otherInvites = (data?.invites ?? []).filter(inv => inv.purpose !== 'pin_message');
  const pinFromList = (data?.invites ?? []).find(inv => inv.purpose === 'pin_message');
  // Prefer the dedicated endpoint's row when present — it's the
  // canonical one (and gets renewed each time the operator opens the
  // page). Otherwise fall back to whatever the list endpoint returned.
  const effectivePin = pinInvite ?? pinFromList;

  function handleChanged() {
    void mutate();
    void mutatePin();
  }

  function handleLeft() {
    setZone('');
    setCamera('');
    setSelectedCamera(null);
    setFrame(null);
    globalMutate(ZONES_SWR_KEY);
    navigate('/');
  }

  function handleCreated(url: string) {
    setLastCreatedUrl(url);
    navigator.clipboard.writeText(url).catch(() => {});
    handleChanged();
  }

  return (
    <div className="space-y-4 p-4 md:p-6 lg:p-8">
      {/* Invite links */}
      <Card>
        <CardContent className="space-y-3">
          <div className="flex items-center justify-between gap-3">
            <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {t('access.linksTitle')}
            </div>
            {canInvite && maxRank > 0 && (
              <Button onClick={() => setCreateOpen(true)} size="sm" variant="outline" className="gap-1.5">
                <Plus className="h-3.5 w-3.5" />
                {t('access.createLink')}
              </Button>
            )}
          </div>
          {effectivePin && (
            <InviteRow
              key={`pin-${effectivePin.id}`}
              invite={effectivePin}
              maxRank={maxRank}
              zoneId={zoneId}
              onChanged={handleChanged}
            />
          )}
          {otherInvites.map(inv => (
            <InviteRow
              key={inv.id}
              invite={inv}
              maxRank={maxRank}
              zoneId={zoneId}
              onChanged={handleChanged}
            />
          ))}
          {loading && <AccessRowSkeleton count={2} />}
          {!loading && !effectivePin && otherInvites.length === 0 && (
            <p className="text-sm text-muted-foreground">{t('access.noLinks')}</p>
          )}
          {lastCreatedUrl && (
            <div className="flex items-center gap-2 rounded-md bg-muted px-3 py-2 text-sm">
              <span className="min-w-0 truncate font-mono">{lastCreatedUrl}</span>
              <span className="shrink-0 text-[11px] text-muted-foreground">{t('access.copied')}</span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Members */}
      <Card>
        <CardContent className="space-y-2">
          <div className="flex items-center justify-between gap-3">
            <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {t('access.members')}{!loading && ` (${members.length})`}
            </div>
            {canInvite && maxRank > 0 && (
              <Button onClick={() => setAddOpen(true)} size="sm" variant="outline" className="gap-1.5">
                <UserPlus className="h-3.5 w-3.5" />
                {t('access.addUser')}
              </Button>
            )}
          </div>
          {loading ? (
            <AccessRowSkeleton count={3} />
          ) : members.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('access.noMembers')}</p>
          ) : (
            members.map(m => (
              <MemberRow
                key={m.id}
                member={m}
                zoneId={zoneId}
                maxRank={maxRank}
                canManage={canInvite}
                isSelf={!!currentUsername && m.username === currentUsername}
                canEditOwnRole={isSuperuser}
                onChanged={handleChanged}
                onLeft={handleLeft}
              />
            ))
          )}
        </CardContent>
      </Card>

      <CreateInviteDialog
        open={createOpen}
        zoneId={zoneId}
        maxRank={maxRank}
        onClose={() => setCreateOpen(false)}
        onCreated={handleCreated}
      />
      <AddUserDialog
        open={addOpen}
        zoneId={zoneId}
        maxRank={maxRank}
        isSuperuser={isSuperuser}
        onClose={() => setAddOpen(false)}
        onChanged={handleChanged}
      />
    </div>
  );
};

export default AccessPage;
